import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Youtube, Instagram, Twitter, Facebook, MessageCircle, Mail, MapPin } from 'lucide-react';
import { siteSettings as mockSettings } from '../data/mock';
import { contentAPI } from '../services/api';

const Footer = () => {
  const [siteSettings, setSiteSettings] = useState(mockSettings);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    contentAPI.get('site_settings').then(res => {
      if (res.data && Object.keys(res.data).length) {
        // Use database data BUT always use hardcoded offices from mockSettings
        setSiteSettings({
          ...res.data,
          offices: mockSettings.offices  // ALWAYS use mock offices, ignore database
        });
      }
    }).catch(() => {});
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Us', path: '/about' },
      { label: 'Team', path: '/team' },
      { label: 'Projects', path: '/projects' },
      { label: 'Newsletter', path: '/newsletter' }
    ],
    services: [
      { label: 'Renewable Consulting', path: '/renewable-consulting' },
      { label: 'Regulatory Consultancy', path: '/regulatory-consultancy' },
      { label: 'Legal Advisory', path: '/legal-advisory' },
      { label: 'All Services', path: '/services' }
    ],
    resources: [
      { label: 'Partners & Customers', path: '/partners-customers' },
      { label: 'Contact Us', path: '/contact' }
    ]
  };

  const socialLinks = [
    { icon: Linkedin, url: siteSettings.socialLinks.linkedin, label: 'LinkedIn' },
    { icon: Youtube, url: siteSettings.socialLinks.youtube, label: 'YouTube' },
    { icon: Instagram, url: siteSettings.socialLinks.instagram, label: 'Instagram' },
    { icon: Twitter, url: siteSettings.socialLinks.twitter, label: 'Twitter' },
    { icon: Facebook, url: siteSettings.socialLinks.facebook, label: 'Facebook' },
    { icon: MessageCircle, url: siteSettings.socialLinks.whatsapp, label: 'WhatsApp' }
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              <img 
                src="/solarian-logo.png" 
                alt="SOLARIAN Logo" 
                className="h-16 w-auto"
              />
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {siteSettings.companyFullName}
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-4">
              Tailored renewable energy solutions and regulatory expertise for developers and EPC companies across India.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3 mb-6">
              <a href={`mailto:${siteSettings.email}`} className="flex items-start gap-3 text-gray-400 hover:text-emerald-400 transition-colors duration-200 group">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-sm">{siteSettings.email}</span>
              </a>
            </div>

            {/* Social Links */}
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-emerald-600 hover:text-white transition-all duration-200 transform hover:scale-110"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links - Company */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links - Services */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Services</h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-emerald-400 transition-colors duration-200 text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Offices */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Our Offices</h3>
            <div className="space-y-4">
              <div>
                <h4 className="text-emerald-400 font-medium text-sm mb-1">{siteSettings.offices?.head?.name || "Head Office"}</h4>
                <a 
                  href={siteSettings.offices?.head?.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 text-xs leading-relaxed flex gap-2 hover:text-emerald-400 transition-colors duration-200 group"
                >
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                  <span>{siteSettings.offices?.head?.address || "Noida, UP"}</span>
                </a>
              </div>
              <div>
                <h4 className="text-emerald-400 font-medium text-sm mb-1">{siteSettings.offices?.haryana?.name || "Haryana Office"}</h4>
                <a 
                  href={siteSettings.offices?.haryana?.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 text-xs leading-relaxed flex gap-2 hover:text-emerald-400 transition-colors duration-200 group"
                >
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                  <span>{siteSettings.offices?.haryana?.address || "Gurugram, Haryana"}</span>
                </a>
              </div>
              <div>
                <h4 className="text-emerald-400 font-medium text-sm mb-1">{siteSettings.offices?.maharashtra?.name || "Maharashtra Office"}</h4>
                <a 
                  href={siteSettings.offices?.maharashtra?.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 text-xs leading-relaxed flex gap-2 hover:text-emerald-400 transition-colors duration-200 group"
                >
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                  <span>{siteSettings.offices?.maharashtra?.address || "Navi Mumbai, MH"}</span>
                </a>
              </div>
              <div>
                <h4 className="text-emerald-400 font-medium text-sm mb-1">{siteSettings.offices?.rajasthan?.name || "Rajasthan Office"}</h4>
                <a 
                  href={siteSettings.offices?.rajasthan?.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 text-xs leading-relaxed flex gap-2 hover:text-emerald-400 transition-colors duration-200 group"
                >
                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-200" />
                  <span>{siteSettings.offices?.rajasthan?.address || "Jaisalmer, RJ"}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ISO Certifications */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-sm text-gray-400 mb-5 uppercase tracking-wider font-medium">ISO Certified Company</p>
          <div className="flex justify-center items-center gap-8 md:gap-12">
            <div className="bg-white rounded-xl p-3 shadow-lg" data-testid="iso-9001">
              <img src="/iso-9001.png" alt="ISO 9001:2015 Certified" className="h-20 md:h-24 w-auto" />
            </div>
            <div className="bg-white rounded-xl p-3 shadow-lg" data-testid="iso-14001">
              <img src="/iso-14001.png" alt="ISO 14001 Certified" className="h-20 md:h-24 w-auto" />
            </div>
            <div className="bg-white rounded-xl p-3 shadow-lg" data-testid="iso-45001">
              <img src="/iso-45001.png" alt="ISO 45001:2018 Certified" className="h-20 md:h-24 w-auto" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              © {currentYear} {siteSettings.companyFullName}. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/contact" className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="/contact" className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-200">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
